import React, { FC, useEffect, useState } from 'react';

// components
import { TextInput } from 'src/components/Inputs';
import { useIsOneTrustLoaded } from 'src/containers/ConsentManager/ConsentManager';

// helpers
import { isUsOrganicFormTraffic } from 'src/lib/is-us-organic-form-traffic';
import { getRequiredMessage } from 'src/lib/utils';

import { useLD } from 'src/contexts/LaunchDarklyContext/LaunchDarklyContext';

import {
  PHONE_REQUIRED_FLAG,
  isPhoneRequiredTreatment,
  usePhoneRequiredVariant,
} from './usePhoneRequiredVariant';

export interface PhoneFieldData {
  label: string;
  name: string;
  describedby?: string;
  autoComplete?: string;
  invalidMessage?: string;
}

export interface PhoneFieldProps {
  data: PhoneFieldData;
  errors?: Record<string, { message?: string }>;
  /* register accepts varying arguments & the ReturnType method fails */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  register?: any;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  isDarkMode?: boolean;
  /**
   * Layouts that mirror the label into the placeholder pass their own helper so
   * the placeholder follows whichever arm's copy is showing.
   */
  getPlaceholder?: (label: string) => string | undefined;
}

const OPTIONAL_LABEL_SUFFIX = /\s*\(optional\)\s*$/i;

/**
 * Digits plus the separators Marketo accepts. The lookahead requires 7-15
 * digits, the E.164 range, so separators on their own and partial numbers fail
 * alongside letters.
 */
export const PHONE_PATTERN = /^(?=(?:\D*\d){7,15}\D*$)\+?[\d\s().-]+$/;

/** `src/store/form` is untyped, so callers can omit their own copy. */
const FALLBACK_INVALID_MESSAGE = 'Phone number is invalid';

/** Treatment copy is the existing label with "(optional)" removed. */
export const getPhoneFieldLabel = (label: string, isRequired: boolean) => {
  if (!isRequired) {
    return label;
  }

  return label.replace(OPTIONAL_LABEL_SUFFIX, '').trim() || label;
};

const PhoneInput: FC<PhoneFieldProps & { isRequired: boolean }> = ({
  data,
  errors,
  register,
  getPlaceholder,
  isRequired,
  ...restProps
}) => {
  const label = getPhoneFieldLabel(data.label, isRequired);

  // Threads PhoneInput is still bugged:
  // [redacted]#################################################################################
  return (
    <TextInput
      {...restProps}
      label={label}
      placeholder={getPlaceholder?.(label)}
      id={data.name}
      name={data.name}
      describedby={data.describedby}
      errorMessage={errors?.[data.name]?.message}
      register={register}
      {...(isRequired && { required: getRequiredMessage(label) })}
      pattern={{
        value: PHONE_PATTERN,
        message: data.invalidMessage ?? FALLBACK_INVALID_MESSAGE,
      }}
      autoComplete={data.autoComplete}
    />
  );
};

/**
 * Isolated so `useLDFlag` (and VIEWED_EXPERIMENT) only fire for eligible
 * visitors. Rendered as a sibling of `PhoneInput` so assigning the experiment
 * does not remount the field and wipe its local value.
 */
const PhoneExperimentExposure: FC = () => {
  usePhoneRequiredVariant();
  return null;
};

/**
 * The Phone field shared by every marketing Marketo form. It is optional today
 * and required for visitors in the `01-phone-number` treatment who are in the
 * US and not on paid search.
 *
 * Layouts stay responsible for whether Phone renders at all (the CMS
 * `includePhone` toggle); the experiment only changes copy and validation.
 */
const PhoneField: FC<PhoneFieldProps> = (props) => {
  const [loaded, setLoaded] = useState(false);
  // Subscribe so we re-read geo once OneTrust injects getGeolocationData.
  useIsOneTrustLoaded();
  const flag = useLD()?.flags?.[PHONE_REQUIRED_FLAG] ?? null;

  useEffect(() => {
    setLoaded(true);
  }, []);

  const eligible = loaded && isUsOrganicFormTraffic();
  const isRequired = eligible && isPhoneRequiredTreatment(flag);

  return (
    <>
      <PhoneInput {...props} isRequired={isRequired} />
      {eligible ? <PhoneExperimentExposure /> : null}
    </>
  );
};

PhoneField.displayName = 'PhoneField';
export default PhoneField;
